import * as Dialog from "@radix-ui/react-dialog";
import { useWindowSize } from "@uidotdev/usehooks";
import { clsx } from "cn";
import { AnimatePresence, motion } from "motion/react";
import { useEffect } from "react";

const transitionVariant = {
  enter: {
    opacity: 1,
    transition: {
      opacity: {
        duration: 0.4,
        ease: [0.36, 0.66, 0.4, 1] as const,
      },
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: [0.36, 0.66, 0.4, 1] as const,
    },
  },
} as const;
export type OverlayProps = React.PropsWithChildren<{
  open: boolean;
  onClose?: () => void;
  className?: string;
  align?: "center" | "top" | "none";
  animation?: boolean;
}>;

export function Overlay({
  open,
  onClose,
  children,
  className,
  align = "top",
  animation = true,
}: OverlayProps) {
  const size = useWindowSize();
  useEffect(() => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }, [size]);
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal forceMount>
        <AnimatePresence>
          {open && (
            <motion.div
              data-overlay="container"
              className="fixed z-50 w-screen h-[calc(var(--vh,1vh)*100)] top-0 left-0"
              initial="exit"
              animate="enter"
              exit="exit"
              variants={animation ? transitionVariant : undefined}
            >
              <Dialog.Overlay
                className={clsx(
                  "absolute w-screen h-[calc(var(--vh,1vh)*100)] bg-bg-dark-low",
                  className,
                )}
                onClick={onClose}
              ></Dialog.Overlay>
              <Dialog.Title className="sr-only">Lightbox</Dialog.Title>
              <Dialog.Content asChild>
                <div
                  role="dialog"
                  aria-modal="true"
                  className={clsx(
                    "!pointer-events-auto focus:outline-none h-fit",
                    {
                      "absolute left-1/2 -translate-x-1/2 bottom-0 sm:top-0":
                        align === "top",
                      "absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2":
                        align === "center",
                      "absolute inset-0": align === "none",
                    },
                  )}
                >
                  {children}
                </div>
              </Dialog.Content>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
